import { PayloadAction, 
         createAsyncThunk, 
         createSlice                       } from '@reduxjs/toolkit';

import { AsyncOpStatus, Nullable           } from 'src/common';

import Api, { ApiError                     } from 'src/services/api';
import * as types                            from 'src/services/api/types';
import { ProductCounterStatusEnum          } from 'src/services/api/types/product-counter';

import { RootState                         } from 'src/store';

const SLICE_NAME = 'main:product-counter';

export interface MainProductCounterState {
  photo: File | null;
  opStatus: AsyncOpStatus;
  statusPolling: boolean;
  results: Nullable<types.productCounter.GetCounterResultOut>;
  resultUploadImage: Nullable<types.productCounter.UploadImageForCounterOut>;
  resultsCounterStatus: Nullable<types.productCounter.GetCounterStatusOut>;
  resultsUploadFB: Nullable<types.productCounter.UploadFBForImageOut>;
  isOpenErrorDialog: boolean;
}

const initialState: MainProductCounterState = {
  photo: null,
  opStatus: AsyncOpStatus.IDLE,
  statusPolling: false,
  results: null,
  resultUploadImage: null,
  resultsCounterStatus: null,
  resultsUploadFB: null,
  isOpenErrorDialog: false,
};

// #region uploadImageForCounter
type UploadImageForCounterArgs = FormData;
type UploadImageForCounterResolve =
  types.productCounter.UploadImageForCounterOut;
export type UploadImageForCounterReject = ApiError;

export const uploadImageForCounterAsync = createAsyncThunk<
  UploadImageForCounterResolve,
  UploadImageForCounterArgs,
  {
    state: RootState;
    rejectValue: UploadImageForCounterReject;
  }
>(`${SLICE_NAME}/uploadImageForCounter`, async (args, thunkAPI) => {
  try {
    thunkAPI.dispatch(
      mainProductCounterActions.opStatusChanged(AsyncOpStatus.BUSY)
    );

    const result = await Api.postUploadImageForCounter(args);

    thunkAPI.dispatch(
      mainProductCounterActions.opStatusChanged(AsyncOpStatus.SUCCESS)
    );

    return result;
  } catch (error) {
    thunkAPI.dispatch(
      mainProductCounterActions.opStatusChanged(AsyncOpStatus.IDLE)
    );
    return thunkAPI.rejectWithValue(error as ApiError);
  }
});
// #endregion

// #region getCounterStatus
export type GetCounterStatusArgs = {
  ticket: string;
};
export type GetCounterStatusResolve = types.productCounter.GetCounterStatusOut;
export type GetCounterStatusReject = ApiError;

export const getCounterStatusAsync = createAsyncThunk<
  GetCounterStatusResolve,
  GetCounterStatusArgs,
  {
    state: RootState;
    rejectValue: UploadImageForCounterReject;
  }
>(`${SLICE_NAME}/getCounterStatusAsync`, async (args, thunkAPI) => {
  try {
    thunkAPI.dispatch(
      mainProductCounterActions.opStatusChanged(AsyncOpStatus.BUSY)
    );

    const result = await Api.getCounterStatus(args);

    thunkAPI.dispatch(
      mainProductCounterActions.opStatusChanged(
        result.status === ProductCounterStatusEnum.PROCESSING
          ? AsyncOpStatus.BUSY
          : AsyncOpStatus.SUCCESS
      )
    );

    return result;
  } catch (error) {
    thunkAPI.dispatch(
      mainProductCounterActions.opStatusChanged(AsyncOpStatus.IDLE)
    );
    return thunkAPI.rejectWithValue(error as ApiError);
  }
});
// #endregion

// #region uploadFBForImage
type UploadFBForImageArgs = { guid: string; numberOfSheets: number };
type UploadFBForImageResolve = types.productCounter.UploadFBForImageOut;
export type UploadFBForImageReject = ApiError;

export const uploadFBForImageAsync = createAsyncThunk<
  UploadFBForImageResolve,
  UploadFBForImageArgs,
  {
    state: RootState;
    rejectValue: UploadFBForImageReject;
  }
>(`${SLICE_NAME}/uploadFBForImage`, async (args, thunkAPI) => {
  try {
    const stateProductCountResultDialog =
      thunkAPI.getState().mainProductCountResultDialog;

    thunkAPI.dispatch(
      mainProductCounterActions.opStatusChanged(AsyncOpStatus.BUSY)
    );

    const isResultOk =
      stateProductCountResultDialog.selectedItem.id === '1' ? 1 : 0;

    const formData = new FormData();
    formData.append('guid', args.guid);
    formData.append('cntItem', args.numberOfSheets.toString());
    formData.append('isResultOk', isResultOk.toString());
    formData.append('commemt', stateProductCountResultDialog.commentValue);

    const results = await Api.postUploadFBForImage(formData);
    thunkAPI.dispatch(
      mainProductCounterActions.opStatusChanged(AsyncOpStatus.IDLE)
    );
    thunkAPI.dispatch(mainProductCounterActions.initState());

    return results;
  } catch (error) {
    thunkAPI.dispatch(
      mainProductCounterActions.opStatusChanged(AsyncOpStatus.IDLE)
    );
    thunkAPI.dispatch(mainProductCounterActions.initState());
    return thunkAPI.rejectWithValue(error as ApiError);
  }
});
// #endregion

export const mainProductCounterSlice = createSlice({
  name: SLICE_NAME,
  initialState: { ...initialState },
  reducers: {
    setPhoto(state, action: PayloadAction<File | null>) {
      state.photo = action.payload;
    },
    opStatusChanged: (state, action: PayloadAction<AsyncOpStatus>) => {
      state.opStatus = action.payload;
    },
    startPolling(state) {
      state.statusPolling = true;
    },
    stopPolling(state) {
      state.statusPolling = false;
    },
    changeIsOpenErrorDialog(state) {
      state.isOpenErrorDialog = !state.isOpenErrorDialog;
    },
    initState: (state) => {
      state.opStatus = AsyncOpStatus.IDLE;
      state.resultUploadImage = null;
      state.resultsCounterStatus = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(uploadImageForCounterAsync.fulfilled, (state, action) => {
      state.resultUploadImage = action.payload;
    });
    builder.addCase(uploadFBForImageAsync.fulfilled, (state, action) => {
      state.resultsUploadFB = action.payload;
    });
    builder.addCase(getCounterStatusAsync.fulfilled, (state, action) => {
      state.resultsCounterStatus = action.payload;
    });
  },
});

const selectPhoto = (state: RootState) => state.mainProductCounter.photo;
const selectOpStatus = (state: RootState) => state.mainProductCounter.opStatus;
const selectResultUploadImage = (state: RootState) =>
  state.mainProductCounter.resultUploadImage;
const selectStatusPolling = (state: RootState) =>
  state.mainProductCounter.statusPolling;
const selectResultsCounterStatus = (state: RootState) =>
  state.mainProductCounter.resultsCounterStatus;
const selectIsOpenErrorDialog = (state: RootState) =>
  state.mainProductCounter.isOpenErrorDialog;

export const mainProductCounterSelectors = {
  selectPhoto,
  selectOpStatus,
  selectStatusPolling,
  selectResultUploadImage,
  selectResultsCounterStatus,
  selectIsOpenErrorDialog,
};

export const mainProductCounterActions = mainProductCounterSlice.actions;

export default mainProductCounterSlice.reducer;
